import { Checkbox } from '../ui/checkbox';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../ui/tooltip';

export interface ChecklistItem {
    key: string;
    label: string;
    description?: string;
    /** Shown on hover over the label instead of inline text. */
    tooltip?: string;
    access?: 'read' | 'write';
}

export interface ChecklistSection {
    label?: string;
    items: ChecklistItem[];
}

interface PermissionChecklistProps {
    sections: ChecklistSection[];
    selected: string[];
    onChange?: (selected: string[]) => void;
    disabled?: boolean;
}

export default function PermissionChecklist({
    sections,
    selected,
    onChange,
    disabled = false,
}: PermissionChecklistProps) {
    const toggle = (key: string) => {
        if (disabled || !onChange) return;
        onChange(
            selected.includes(key)
                ? selected.filter((k) => k !== key)
                : [...selected, key]
        );
    };

    return (
        <TooltipProvider delayDuration={300}>
        <div className="space-y-4">
            {sections.map((section, i) => (
                <div key={section.label ?? i} className="space-y-1.5">
                    {section.label && (
                        <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
                            {section.label}
                        </p>
                    )}
                    <div className="flex flex-col gap-1.5">
                        {section.items.map((item) => (
                            <label
                                key={item.key}
                                className={`flex items-center gap-2.5 rounded-md border border-white/[0.06] bg-white/[0.03] px-3 py-2 text-sm ${
                                    disabled
                                        ? 'opacity-60'
                                        : 'cursor-pointer hover:bg-white/[0.05]'
                                }`}
                            >
                                <Checkbox
                                    checked={selected.includes(item.key)}
                                    onCheckedChange={() => toggle(item.key)}
                                    disabled={disabled}
                                />
                                <span className="flex-1 min-w-0">
                                    {item.tooltip ? (
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className="block w-fit text-foreground">
                                                    {item.label}
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent
                                                side="top"
                                                className="max-w-[240px] text-xs"
                                            >
                                                {item.tooltip}
                                            </TooltipContent>
                                        </Tooltip>
                                    ) : (
                                        <span className="block text-foreground">
                                            {item.label}
                                        </span>
                                    )}
                                    {item.description && (
                                        <span className="block truncate text-xs text-muted-foreground">
                                            {item.description}
                                        </span>
                                    )}
                                </span>
                                {item.access && (
                                    <span
                                        className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium leading-none ${
                                            item.access === 'write'
                                                ? 'bg-amber-500/15 text-amber-400'
                                                : 'bg-sky-500/15 text-sky-400'
                                        }`}
                                    >
                                        {item.access === 'write'
                                            ? 'Write'
                                            : 'Read'}
                                    </span>
                                )}
                            </label>
                        ))}
                    </div>
                </div>
            ))}
        </div>
        </TooltipProvider>
    );
}
